import { doc, setDoc } from 'firebase/firestore';
import { db } from './firebase';

export function getMessageConversationId(docSnap) {
  const data = docSnap.data();
  return data.conversationId || docSnap.id;
}

export function buildConversationList(docs, userId) {
  const uniqueConversations = {};

  docs.forEach((docSnap) => {
    const data = docSnap.data();
    const convId = getMessageConversationId(docSnap);
    const timestamp = data.timestamp || data.createdAt;
    const existing = uniqueConversations[convId];

    if (!existing || new Date(timestamp || 0) > new Date(existing.timestamp || 0)) {
      uniqueConversations[convId] = {
        id: convId,
        gearId: data.gearId,
        conversationType: data.conversationType,
        gearName: data.gearName || (data.conversationType === 'local-expert' ? 'Local Area Guide' : 'Gear Rental'),
        otherUserId: data.senderId === userId ? data.recipientId : data.senderId,
        otherUserName: data.otherUserName || 'Other User',
        lastMessage: data.text || '',
        lastMessageSenderId: data.senderId || '',
        timestamp,
      };
    }
  });

  return Object.values(uniqueConversations);
}

export function computeUnreadState(conversations, conversationReads, userId) {
  const unreadConversationIds = new Set();

  conversations.forEach((conv) => {
    if (!conv.lastMessage || conv.lastMessageSenderId === userId) return;

    const readState = conversationReads[conv.id];
    const lastEngagedAt = readState?.lastEngagedAt || readState?.lastReadAt;
    const messageAt = conv.timestamp;

    if (!lastEngagedAt || new Date(messageAt || 0) > new Date(lastEngagedAt)) {
      unreadConversationIds.add(conv.id);
    }
  });

  return {
    unreadCount: unreadConversationIds.size,
    unreadConversationIds,
  };
}

export async function markConversationEngaged(userId, conversationId) {
  if (!userId || !conversationId) return;

  const now = new Date().toISOString();
  await setDoc(
    doc(db, 'users', userId, 'conversationReads', conversationId),
    { lastReadAt: now, lastEngagedAt: now },
    { merge: true }
  );
}

export async function touchMessagingEngagement(userId) {
  if (!userId) return;

  await setDoc(
    doc(db, 'users', userId),
    { lastMessagingEngagementAt: new Date().toISOString() },
    { merge: true }
  );
}

export function isSameMessageThread(data, activeConv) {
  const convId = data.conversationId || '';
  if (convId && convId === activeConv.id) return true;
  if (activeConv.conversationType === 'local-expert') return convId === activeConv.id;
  if (activeConv.conversationType === 'gear-wanted-alert') return convId === activeConv.id;

  if (!activeConv.otherUserId) return false;

  const participants = data.participants || [];
  if (!participants.includes(activeConv.otherUserId)) return false;

  if (activeConv.gearId && data.gearId && data.gearId !== activeConv.gearId) return false;
  if (!activeConv.gearId || !data.gearId) return false;

  return true;
}

export function getThreadMessages(docs, activeConv) {
  if (!activeConv?.id) return [];

  return docs
    .filter((docSnap) => isSameMessageThread(docSnap.data(), activeConv))
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
}

export function findActiveConversationInList(conversations, activeConv) {
  if (!activeConv) return null;

  const direct = conversations.find((conv) => conv.id === activeConv.id);
  if (direct) return direct;

  return conversations.find((conv) =>
    conv.otherUserId === activeConv.otherUserId &&
    conv.gearId === activeConv.gearId &&
    conv.conversationType === activeConv.conversationType
  ) || null;
}